import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input from '@ferris/react/components/forms/Input';
import Button from '@ferris/react/components/button/Button';
import PhoneInputWrapper from '../../Input/PhoneInputWrapper';
import Fields from '../../ListYourPropertyPage/Location/ManualLocation/Fields';
import { addressFormats } from '../../ListYourPropertyPage/Location/AutoComplete/AddressFormats';

// eslint-disable-next-line max-len
export const getFormInput = (forItem, label, type, value, changeHandler, error, handleBlur, iconName) => (
    <Input
        id={`${forItem}-input`}
        fullWidth
        label={label}
        type={type}
        isControlled
        floatLabel
        data-wdio={`${forItem}-input`}
        value={value}
        onChange={changeHandler}
        invalid={error && error.length}
        validationMessage={error || ''}
        onBlur={handleBlur}
        aria-label={label}
        aria-invalid={!!error}
        iconName={iconName}
    />
);

function LeadForm(props) {
    const {
        leadFormTitle,
        leadFormDescription,
        firstNameLabel,
        firstName,
        firstNameValidation,
        setFirstName,
        lastNameLabel,
        lastName,
        lastNameValidation,
        setLastName,
        emailLabel,
        email,
        setEmail,
        address,
        countryList,
        addressValidation,
        getTranslation,
        brand,
        locale,
        setAddressField,
        closeLabel,
        setShowVacasa,
        leadFormSubmit,
        leadFormPolicy,
        phoneValidation,
        emailValidation,
        validateEmail,
        emailValidationErrorText,
        onSubmit,
        isLoading
    } = props;
    const [isBlured, setIsBlured] = useState({
        FIRST_NAME: false,
        LAST_NAME: false,
        EMAIL: false
    });
    const onChange = (event, callback) => {
        callback(event.target.value);
    };
    const onBlur = (type) => {
        setIsBlured((prevState) => ({
            ...prevState,
            [type]: true
        }));
    };
    const handleEmailBlur = (e) => {
        onBlur('EMAIL');
        if (emailValidation.isValid) {
            validateEmail(e.target.value);
        }
    };
    const handleAddressFieldChange = (e) => {
        setAddressField(e.target.name, e.target.value);
    };

    const isPhoneValidFromService = () => {
        const { validationService } = phoneValidation;
        return validationService && validationService.success && validationService.isValid;
    };

    const isEmailValidFromService = () => {
        const { validationService } = emailValidation;
        return validationService
            && (
                (validationService.start && !validationService.fail && !validationService.success)
                || (validationService.success && validationService.isValid)
            );
    };

    const getValidationErrorMessage = (type) => {
        if (type === 'FIRST_NAME' && isBlured[type]) {
            return firstNameValidation.error;
        }
        if (type === 'LAST_NAME' && isBlured[type]) {
            return lastNameValidation.error;
        }
        if (type === 'EMAIL' && isBlured[type]) {
            const errorFromService = !isEmailValidFromService()
                ? emailValidationErrorText
                : undefined;
            return emailValidation.error
                ? emailValidation.error
                : errorFromService;
        }
        return undefined;
    };

    const isFormDataValid = () => firstNameValidation.isValid
            && lastNameValidation.isValid
            && emailValidation.isValid
            && addressValidation.address1.isValid
            && addressValidation.city.isValid
            && addressValidation.postalCode.isValid
            && addressValidation.country.isValid
            && addressValidation.stateProvince.isValid
            && phoneValidation.isValid
            && isPhoneValidFromService()
            && isEmailValidFromService()
            && !isLoading;

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (email) {
            handleEmailBlur({ target: { value: email } });
        }
    }, [email]);

    const fieldsProps = {
        order: addressFormats.default.order,
        ignoreRTL: addressFormats.default.ignoreRTL,
        unitNumberValidation: {
            value: {
                missingUnitNumber: false,
                number: '',
                unitNumberValid: true
            }
        },
        unitCheckEnabled: false,
        address,
        countryList,
        addressValidation,
        serverAddressValidation: {},
        handleAddressFieldChange,
        getTranslation,
        setConfirmNoUnitNumber: () => {},
        confirmNoUnitNumber: false,
        brand,
        locale: locale.canonical
    };

    return (
        <div className="ulx-pm-lead-container">
            <h3>{leadFormTitle}</h3>
            <p>{leadFormDescription}</p>
            <div className="ulx-pm-lead-components">
                {getFormInput('compLead-first-name', firstNameLabel, 'text', firstName, (e) => onChange(e, setFirstName), getValidationErrorMessage('FIRST_NAME'), () => onBlur('FIRST_NAME'))}
            </div>
            <div className="ulx-pm-lead-components">
                {getFormInput('compLead-last-name', lastNameLabel, 'text', lastName, (e) => onChange(e, setLastName), getValidationErrorMessage('LAST_NAME'), () => onBlur('LAST_NAME'))}
            </div>
            <div className="ulx-pm-lead-components">
                {getFormInput('compLead-email', emailLabel, 'email', email, (e) => onChange(e, setEmail), getValidationErrorMessage('EMAIL'), handleEmailBlur, 'email')}
            </div>
            <PhoneInputWrapper />
            <Fields {...fieldsProps} isVacasa />
            <div className="ulx-pm-lead-cta-container">
                <Button
                    htmlButtonType="button"
                    type="primary"
                    onClick={() => onSubmit()}
                    disabled={!isFormDataValid()}
                >
                    {leadFormSubmit}
                </Button>
                <Button
                    htmlButtonType="button"
                    small
                    type="utility"
                    variant="utility-standard"
                    onClick={() => setShowVacasa(false)}
                >
                    {closeLabel}
                </Button>
            </div>
            <p className="ulx-pm-lead-policy">{leadFormPolicy}</p>
        </div>
    );
}

const ValidationObject = {
    isValid: PropTypes.bool.isRequired,
    error: PropTypes.string
};

LeadForm.propTypes = {
    leadFormTitle: PropTypes.string.isRequired,
    leadFormDescription: PropTypes.string.isRequired,
    firstNameLabel: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    firstNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setFirstName: PropTypes.func.isRequired,
    lastNameLabel: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    lastNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setLastName: PropTypes.func.isRequired,
    emailLabel: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    setEmail: PropTypes.func.isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        })
    }).isRequired,
    getTranslation: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    setAddressField: PropTypes.func.isRequired,
    setShowVacasa: PropTypes.func.isRequired,
    closeLabel: PropTypes.string.isRequired,
    leadFormSubmit: PropTypes.string.isRequired,
    leadFormPolicy: PropTypes.string.isRequired,
    phoneValidation: PropTypes.shape(ValidationObject).isRequired,
    emailValidation: PropTypes.shape(ValidationObject).isRequired,
    validateEmail: PropTypes.func.isRequired,
    emailValidationErrorText: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired
};

export default LeadForm;
